import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import {
  RefundReason,
  RefundStatus,
  type RefundReason as RefundReasonT,
  type RefundStatus as RefundStatusT,
} from '@etiquetachef/types';
import {
  subscribeCrossTenantRefunds,
  type RefundListResult,
} from '@/lib/firestoreTenantDetail';
import { FinanceFilters } from '@/features/finance/FinanceFilters';
import { RefundsTable } from '@/features/finance/RefundsTable';
import { RequestRefundDialog } from '@/features/finance/RequestRefundDialog';

const STATUS_OPTIONS: { value: RefundStatusT; labelKey: string }[] = [
  { value: RefundStatus.PENDING, labelKey: 'admin.finance.refunds.status.PENDING' },
  { value: RefundStatus.SUCCEEDED, labelKey: 'admin.finance.refunds.status.SUCCEEDED' },
  { value: RefundStatus.FAILED, labelKey: 'admin.finance.refunds.status.FAILED' },
];

const REASON_OPTIONS: { value: RefundReasonT; labelKey: string }[] = [
  { value: RefundReason.COMMERCIAL, labelKey: 'admin.finance.refunds.reason.COMMERCIAL' },
  { value: RefundReason.REQUESTED_BY_SUPPORT, labelKey: 'admin.finance.refunds.reason.REQUESTED_BY_SUPPORT' },
  { value: RefundReason.DUPLICATE, labelKey: 'admin.finance.refunds.reason.DUPLICATE' },
  { value: RefundReason.FRAUDULENT, labelKey: 'admin.finance.refunds.reason.FRAUDULENT' },
  { value: RefundReason.OTHER, labelKey: 'admin.finance.refunds.reason.OTHER' },
  { value: RefundReason.MBG, labelKey: 'admin.finance.refunds.reason.MBG' },
  { value: RefundReason.DISPUTE, labelKey: 'admin.finance.refunds.reason.DISPUTE' },
];

/**
 * Tela `/finance/refunds` — reembolsos de todas as empresas, com filtro por
 * status e motivo, busca local e "Solicitar reembolso".
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 (Sprint 5 PR H, painel
 * financeiro).
 *
 * Contrato: assina `subscribeCrossTenantRefunds({ status, reason })`; a busca
 * (tenant, pagamento, id) filtra no cliente o que já veio. O pedido de
 * reembolso é do `RequestRefundDialog` (callable no servidor, que fala com o
 * PSP). Valores em centavos, convertidos no `RefundsTable`.
 *
 * Usado por: rota `finance/refunds` (`routes/router.tsx`), menu Cobrança
 * (`AdminLayout`). Spec: `docs/screens/finance-refunds.md`.
 *
 * Dados: collection group `refunds`, `orderBy('requested_at','desc')` com
 * `status` e/ou `reason` por igualdade; índices `COLLECTION_GROUP` `status +
 * requested_at` e `reason + requested_at`; rule `match
 * /{path=**}/refunds/{refundId}` (só super-admin).
 *
 * Dicas: status E motivo juntos não têm índice composto próprio — dependem de
 * o Firestore combinar os dois acima. Se vier `failed-precondition`, falta
 * `status + reason + requested_at`.
 */
export function RefundsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<RefundStatusT | null>(null);
  const [reason, setReason] = useState<RefundReasonT | null>(null);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<RefundListResult>({ kind: 'LOADING' });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setData({ kind: 'LOADING' });
    return subscribeCrossTenantRefunds({ status, reason }, setData);
  }, [status, reason]);

  const filtered = useMemo(() => {
    if (data.kind !== 'READY') return [];
    const needle = search.trim().toLowerCase();
    if (needle === '') return data.refunds;
    return data.refunds.filter(
      (r) =>
        r.tenant_id.toLowerCase().includes(needle) ||
        r.payment_id.toLowerCase().includes(needle) ||
        r.id.toLowerCase().includes(needle),
    );
  }, [data, search]);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('admin.finance.refunds.subtitle')}
          actions={
            <Button
              variant="primary"
              onClick={(): void => {
                setDialogOpen(true);
              }}
            >
              {t('admin.finance.refunds.requestButton')}
            </Button>
          }
        >
          {t('admin.finance.refunds.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">{t('admin.finance.filters.title')}</Header>}>
          <FinanceFilters
            statusValue={status}
            statusOptions={STATUS_OPTIONS}
            onStatusChange={setStatus}
            secondaryValue={reason}
            secondaryOptions={REASON_OPTIONS}
            onSecondaryChange={setReason}
            secondaryLabel={t('admin.finance.filters.reason')}
            searchValue={search}
            onSearchChange={setSearch}
          />
        </Container>

        {data.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {data.kind === 'ERROR' && <Alert type="error">{t('admin.finance.refunds.errorLoad')}</Alert>}
        {data.kind === 'READY' && <RefundsTable refunds={filtered} locale={i18n.language} />}
      </SpaceBetween>

      <RequestRefundDialog
        open={dialogOpen}
        onClose={(): void => {
          setDialogOpen(false);
        }}
      />
    </ContentLayout>
  );
}
